import React, { useEffect, useState } from 'react'
import { User, AuthResponse, LoginRequest, RegisterRequest } from '../types/auth'
import { getErrorMessage } from '../types/api'
import { apiClient } from '../services/api'
import { AuthContext } from './AuthContext'

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check for existing token on app load
    const storedToken = localStorage.getItem('token')
    if (storedToken) {
      setToken(storedToken)
      fetchCurrentUser()
    } else {
      setIsLoading(false)
    }
  }, [])

  const fetchCurrentUser = async () => {
    try {
      const response = await apiClient.get('/auth/me')
      setUser({
        userId: response.data.userId,
        email: response.data.email,
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        role: response.data.role,
        companyId: response.data.companyId,
        companyName: response.data.companyName,
        isActive: response.data.isActive,
        createdAt: response.data.createdAt
      })
    } catch (error) {
      console.error('Failed to fetch current user:', error)
      // Token might be invalid, clear it
      localStorage.removeItem('token')
      setToken(null)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (credentials: LoginRequest) => {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/login', credentials)
      const { token: newToken, ...userData } = response.data
      
      localStorage.setItem('token', newToken)
      setToken(newToken)
      setUser({
        userId: userData.userId,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: userData.role as User['role'],
        companyId: userData.companyId,
        companyName: userData.companyName,
        isActive: true,
        createdAt: new Date().toISOString()
      })
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Login failed'))
    }
  }

  const register = async (userData: RegisterRequest) => {
    try {
      const response = await apiClient.post<AuthResponse>('/auth/register', userData)
      const { token: newToken, ...userInfo } = response.data
      
      localStorage.setItem('token', newToken)
      setToken(newToken)
      setUser({
        userId: userInfo.userId,
        email: userInfo.email,
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        role: userInfo.role as User['role'],
        companyId: userInfo.companyId,
        companyName: userInfo.companyName,
        isActive: true,
        createdAt: new Date().toISOString()
      })
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Registration failed'))
    }
  }

  const logout = () => {
    localStorage.removeItem('token')
    setToken(null)
    setUser(null)
    
    // Call logout endpoint
    apiClient.post('/auth/logout').catch(() => {
      // Ignore errors on logout
    })
    
    // Force reload to clear all state
    window.location.href = '/login'
  }

  const value = {
    user,
    token,
    login,
    register,
    logout,
    isLoading,
    isAuthenticated: !!user && !!token
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}